import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';

// The sample uses whole USD amounts. Windows Hermes can return six decimal
// places from toLocaleString, so group these bounded integers directly.
const budgetAmount = value => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export function discoverySampleKind(idea) {
  const title = String(idea.title || '').toLowerCase();
  if (/pasta|pancake|cookies|frittata|lentil stew|salmon|flatbread|apple crumble|noodle bowls/.test(title)) return 'recipe';
  if (/geography|quiz|trivia/.test(title)) return 'quiz';
  if (idea.id === 'spreadsheet' || /budget/.test(title)) return 'budget';
  return null;
}
export default function DiscoverySample({ idea, palette, onSelect }) {
  const kind = discoverySampleKind(idea);
  const [servings, setServings] = useState(2), [answer, setAnswer] = useState(null), [spending, setSpending] = useState(1800);
  const button = (label, action, disabled = false, selected = false, display = label) => <Pressable key={label} accessibilityRole="button" accessibilityLabel={label}
    accessibilityState={{ disabled, selected }} disabled={disabled} onPress={action} style={[styles.button, { borderColor: palette.border, backgroundColor: selected ? palette.hover : palette.surface }]}>
    <Text size="s">{display}</Text>
  </Pressable>;
  const recipes = [
    [/cookie/i, 'Cookie dough', 'g flour', 120], [/pancake/i, 'Pancake batter', 'g flour', 100],
    [/frittata/i, 'Spinach frittata', 'eggs', 4], [/lentil/i, 'Lentil stew', 'g dry lentils', 160],
    [/salmon/i, 'Salmon and potatoes', 'g salmon', 300], [/flatbread/i, 'Homemade flatbread', 'g flour', 200],
    [/crumble/i, 'Apple crumble', 'apples', 2], [/noodle/i, 'Tofu noodle bowls', 'g noodles', 180],
  ];
  const match = recipes.find(([pattern]) => pattern.test(idea.title));
  const recipe = match ? match.slice(1) : ['Lemon pasta', 'g pasta', 180];
  const draft = kind === 'recipe' ? `${idea.prompt}\nScale the recipe to ${servings} servings.` : kind === 'budget' ? `${idea.prompt}\nStart with example monthly income of $3,000 and expenses of $${budgetAmount(spending)}; let me edit both.` : idea.prompt;
  return <View style={[styles.root, { backgroundColor: palette.hover }]} testID={`discovery-sample-${kind}`}>
    <Text size="xs" color="secondary">TRY A SAMPLE</Text>
    {kind === 'recipe' && <>
      <Text weight="medium" size="m">{recipe[0]}</Text>
      <View style={styles.row}>{button('Fewer servings', () => setServings(n => n - 1), servings <= 1, false, '−')}<Text weight="medium">{servings} servings</Text>{button('More servings', () => setServings(n => n + 1), servings >= 8, false, '+')}</View>
      <Text size="s" accessibilityLiveRegion="polite">{recipe[2] * servings / 2} {recipe[1]} · quantities scale with you</Text>
    </>}
    {kind === 'quiz' && <>
      <Text weight="medium" size="m">Which ocean is the largest?</Text>
      <View style={styles.row}>{['Atlantic', 'Pacific', 'Indian'].map(name => button(name, () => setAnswer(name), false, answer === name))}</View>
      <Text size="s" accessibilityLiveRegion="polite">{answer ? answer === 'Pacific' ? 'Correct — the Pacific Ocean.' : 'Try again — think bigger.' : 'One question. Give it a try.'}</Text>
    </>}
    {kind === 'budget' && <>
      <Text weight="medium" size="m">Make room in your budget</Text>
      <Text size="s">Example income · $3,000 / month</Text>
      <View style={styles.row}>{button('Spend $100 less', () => setSpending(n => n - 100), spending <= 0, false, '− $100')}{button('Spend $100 more', () => setSpending(n => n + 100), spending >= 3000, false, '+ $100')}</View>
      {Platform.OS === 'web' && <input type="range" aria-label="Example monthly spending" min="0" max="3000" step="100" value={spending} onChange={event => setSpending(Number(event.target.value))} style={{ width: '100%', accentColor: palette.icon }} />}
      <Text weight="medium" accessibilityLiveRegion="polite">${budgetAmount(3000 - spending)} left · ${budgetAmount(spending)} expenses</Text>
    </>}
    <Pressable accessibilityRole="button" accessibilityLabel={`Use this sample: ${idea.title}`} onPress={() => onSelect({ ...idea, prompt: draft })} style={styles.use}>
      <Text size="s" weight="medium">Make it yours ↗</Text>
    </Pressable>
  </View>;
}
const styles = StyleSheet.create({
  root: { padding: 22, gap: 14, minHeight: 220, flexGrow: 1, justifyContent: 'center' },
  row: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  button: { minHeight: 40, minWidth: 40, alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, borderWidth: 1, borderRadius: 10, justifyContent: 'center' },
  use: { alignSelf: 'flex-start', minHeight: 40, justifyContent: 'center' },
});
